import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

const PageHome = lazy(() => import('src/pages/dashboard/home-page'));

const PageEvent = lazy(() => import('src/pages/dashboard/event/event-page'));

const PageProfile = lazy(() => import('src/pages/dashboard/profile/profile'));
const PageInvites = lazy(() => import('src/pages/dashboard/invites/invites'));
const PageIntegration = lazy(() => import('src/pages/dashboard/integration/integration-page'));

const PageMentoring = lazy(() => import('src/pages/dashboard/mentoring/mentoring-page'));
const PageCommunity = lazy(() => import('src/pages/dashboard/community/community-page'));
const PageCommunityNew = lazy(() => import('src/pages/dashboard/community/community-new'));
const PageCommunityEdit = lazy(() => import('src/pages/dashboard/community/community-edit'));
const PageCommunityMembers = lazy(() => import('src/pages/dashboard/community/community-members'));
const PageCommunityInvite = lazy(() => import('src/pages/dashboard/community/community-invite'));
const PageCommunityMemberEdit = lazy(() => import('src/pages/dashboard/community/member-edit'));

const PageToken = lazy(() => import('src/pages/dashboard/token/token-page'));
const PageTokenNew = lazy(() => import('src/pages/dashboard/token/token-new'));
const PageTokenEdit = lazy(() => import('src/pages/dashboard/token/token-edit'));

const PageRepoStats = lazy(() => import('src/pages/admin/repository-stats'));
const PageBackup = lazy(() => import('src/pages/admin/backup'));
const PageProfiles = lazy(() => import('src/pages/admin/profiles/profile-page'));
const PageConfiguration = lazy(() => import('src/pages/admin/configuration/configuration'));

const PageRewardProgram = lazy(
  () => import('src/pages/dashboard/rewardprogram/rewardprogram-page')
);
const PageRewardProgramNew = lazy(
  () => import('src/pages/dashboard/rewardprogram/rewardprogram-new')
);
const PageRewardProgramEdit = lazy(
  () => import('src/pages/dashboard/rewardprogram/rewardprogram-edit')
);

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { path: 'home', element: <PageHome /> },

      {
        path: 'event',
        children: [{ element: <PageEvent />, index: true }],
      },

      { path: 'profile', element: <PageProfile /> },
      { path: 'invites', element: <PageInvites /> },
      { path: 'integration', element: <PageIntegration /> },
      { path: 'mentoring', element: <PageMentoring /> },

      {
        path: 'community',
        children: [
          { element: <PageCommunity />, index: true },
          { path: 'new', element: <PageCommunityNew /> },
          { path: ':id/edit', element: <PageCommunityEdit /> },
          { path: ':id/members', element: <PageCommunityMembers /> },
          { path: ':id/invite', element: <PageCommunityInvite /> },
          { path: 'memberedit/:id', element: <PageCommunityMemberEdit /> },
        ],
      },
      {
        path: 'token',
        children: [
          { element: <PageToken />, index: true },
          { path: 'new', element: <PageTokenNew /> },
          { path: ':id/edit', element: <PageTokenEdit /> },
        ],
      },
      {
        path: 'rewardprogram',
        children: [
          { element: <PageRewardProgram />, index: true },
          { path: 'new', element: <PageRewardProgramNew /> },
          { path: ':id/edit', element: <PageRewardProgramEdit /> },
        ],
      },
    ],
  },
  {
    path: 'admin',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { path: 'repostatus', element: <PageRepoStats />, index: true },
      { path: 'profiles', element: <PageProfiles /> },
      { path: 'configuration', element: <PageConfiguration /> },
      { path: 'backup', element: <PageBackup /> },
    ],
  },
];
